import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Plus, AlertCircle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

interface AddUrlDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  siteUrl: string;
  onSuccess: () => void;
}

export function AddUrlDialog({
  open,
  onOpenChange,
  siteId,
  siteUrl,
  onSuccess
}: AddUrlDialogProps) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateUrl = (urlString: string): boolean => {
    try {
      const urlObj = new URL(urlString);
      const siteUrlObj = new URL(siteUrl);
      
      // Check if the URL belongs to the same domain
      if (urlObj.hostname !== siteUrlObj.hostname) {
        setError('URL must belong to the same domain as the site');
        return false;
      }
      
      return true;
    } catch (e) {
      setError('Please enter a valid URL');
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateUrl(url)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/page-manager/sites/${siteId}/add-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add URL');
      }

      const result = await response.json();
      
      toast({
        title: 'URL Added Successfully',
        description: `Processing has started for ${url}. This may take a few minutes.`,
      });

      // Reset form and close dialog
      setUrl('');
      onOpenChange(false);
      onSuccess();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add URL');
      console.error('Error adding URL:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New URL</DialogTitle>
            <DialogDescription>
              Add a specific URL from your site that may have been missed during crawling. 
              The page will be fully analyzed including screenshots, semantic analysis, and image extraction.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="url">Page URL</Label>
              <Input
                id="url"
                type="url"
                placeholder={`${siteUrl}/example-page`}
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError(null);
                }}
                disabled={loading}
                required
              />
              <p className="text-sm text-muted-foreground">
                Enter the full URL of the page you want to add
              </p>
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !url}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add URL
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}